"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONFIG_ROW_FIELD_COLUMNS = exports.CONFIG_ROW_TITLE_COLUMN = void 0;
exports.isJsonConfigRowField = isJsonConfigRowField;
exports.encodeConfigRowField = encodeConfigRowField;
exports.decodeConfigRowField = decodeConfigRowField;
exports.configRowFieldCells = configRowFieldCells;
exports.configRowFromCells = configRowFromCells;
const zod_1 = require("zod");
const view_config_row_1 = require("./view-config-row");
/**
 * The config row of an App Container as its container row stores it: the
 * title is the name, `view-config:<field>` cells hold the config row fields
 * (objects as JSON text), the settings come from the Config tables. Server
 * and client encode and decode the row here only.
 */
exports.CONFIG_ROW_TITLE_COLUMN = 'title';
/** Fields whose value is an object or record, derived from the row schema. */
const OBJECT_FIELDS = (() => {
    const fields = new Set();
    for (const def of view_config_row_1.VIEW_CONFIG_COLUMN_DEFS) {
        let t = def.zodType;
        while (t instanceof zod_1.z.ZodNullable || t instanceof zod_1.z.ZodOptional) {
            t = t.unwrap();
        }
        if (t instanceof zod_1.z.ZodObject || t instanceof zod_1.z.ZodRecord) {
            fields.add(String(def.field));
        }
    }
    return fields;
})();
const JSON_FIELDS = new Set([
    ...OBJECT_FIELDS,
    'bindings',
    'contentDbRef',
]);
/** Config row fields stored as cells of the container row, by field. */
exports.CONFIG_ROW_FIELD_COLUMNS = new Map(
// The name is the title column, the viewId the row id.
view_config_row_1.VIEW_CONFIG_COLUMN_DEFS.filter(def => def.field !== 'name' && def.field !== 'viewId').map(def => [
    String(def.field),
    {
        id: (0, view_config_row_1.getViewConfigColumnId)(def.field),
        type: def.blockSuiteType,
        data: def.data,
    },
]));
function isJsonConfigRowField(field) {
    return JSON_FIELDS.has(field);
}
function encodeConfigRowField(field, value) {
    if (value === undefined)
        return null;
    if (JSON_FIELDS.has(field))
        return value === null ? null : JSON.stringify(value);
    return value;
}
/**
 * The stored cell of a field as its value. An empty cell is the field's
 * empty value; JSON text that does not parse makes the row unreadable.
 */
function decodeConfigRowField(field, raw, rowId) {
    if (!JSON_FIELDS.has(field))
        return raw;
    if (raw === null || raw === undefined || raw === '') {
        return field === 'bindings' ? {} : null;
    }
    if (typeof raw !== 'string')
        return raw;
    try {
        return JSON.parse(raw);
    }
    catch (error) {
        throw new view_config_row_1.ViewConfigRowInvalidError([
            {
                code: 'custom',
                path: [field],
                message: `Config row ${rowId}: field ${field} holds no valid JSON (${error instanceof Error ? error.message : String(error)})`,
            },
        ]);
    }
}
/**
 * Cells of the container row a config row, or a patch of it, sets besides
 * the settings: the title (name) and the config row fields, encoded.
 */
function configRowFieldCells(row) {
    const cells = {};
    if (typeof row.name === 'string')
        cells[exports.CONFIG_ROW_TITLE_COLUMN] = row.name;
    for (const [field, value] of Object.entries(row)) {
        const column = exports.CONFIG_ROW_FIELD_COLUMNS.get(field);
        if (column)
            cells[column.id] = encodeConfigRowField(field, value);
    }
    return cells;
}
/**
 * The config row: the stored container cells overlaid with the patch, with
 * the given settings. Throws `ViewConfigRowInvalidError` for an unreadable
 * row.
 */
function configRowFromCells(viewId, cell, appBaseConfig, patch = {}) {
    const raw = { viewId, appBaseConfig };
    const title = cell(exports.CONFIG_ROW_TITLE_COLUMN);
    raw.name =
        'name' in patch && typeof patch.name === 'string'
            ? patch.name
            : typeof title === 'string'
                ? title
                : '';
    for (const [field, column] of exports.CONFIG_ROW_FIELD_COLUMNS) {
        if (field in patch) {
            const patched = patch[field];
            raw[field] = patched === undefined ? null : patched;
            continue;
        }
        const stored = cell(column.id);
        if (stored !== undefined)
            raw[field] = decodeConfigRowField(field, stored, viewId);
    }
    return (0, view_config_row_1.parseRow)(raw);
}
